import React, { useMemo, useState } from "react";
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
    PieChart, Pie, Cell, LabelList
} from "recharts";
import { parseToGB, normalizeStatus } from "./utils";

const PIE_COLORS = { active: "#10b981", inactive: "#f43f5e", servers: "#6366f1" };

// Every category row gets this much vertical room, so long server / provider
// names never collide. The card scrolls vertically once rows exceed its height.
const ROW_HEIGHT = 64;
const CARD_HEIGHT = 340;
const chartHeight = (rows) => Math.max(CARD_HEIGHT - 70, rows * ROW_HEIGHT + 60);

const truncate = (v, n = 18) => (String(v).length > n ? `${String(v).slice(0, n - 1)}…` : String(v));

// Card wrapper: fixed height + vertical scroll so charts never overlap.
const ChartCard = ({ title, subtitle, children, footer }) => (
    <div className="rounded-xl border border-slate-200 bg-slate-50/40 p-4 flex flex-col">
        <div className="mb-2">
            <h3 className="text-sm font-bold text-slate-700">{title}</h3>
            {subtitle && <p className="text-[11px] text-slate-400 mt-0.5">{subtitle}</p>}
        </div>
        <div className="overflow-y-auto overflow-x-hidden att-scrollbar rounded-lg bg-white border border-slate-100" style={{ height: CARD_HEIGHT }}>
            {children}
        </div>
        {footer}
    </div>
);

const UsageChips = ({ items, nameKey, pctKey }) => (
    <div className="flex flex-wrap gap-1.5 mt-3 max-h-16 overflow-y-auto">
        {items.map((item) => (
            <div key={item[nameKey] + String(item.server_port ?? "")} className="text-[11px] bg-white border border-slate-200 px-2.5 py-1 rounded-full">
                <span className="font-semibold text-slate-700">{truncate(item[nameKey], 16)}</span>
                <span className={`ml-1.5 font-bold ${item[pctKey] > 80 ? "text-red-600" : item[pctKey] > 50 ? "text-amber-600" : "text-emerald-600"}`}>
                    {item[pctKey]}% used
                </span>
            </div>
        ))}
    </div>
);

const DashboardCharts = ({ servers, flatUsers, onShowGroup }) => {
    const [serverFilter, setServerFilter] = useState("all");
    const [providerFilter, setProviderFilter] = useState("all");
    const [monthFilter, setMonthFilter] = useState("all");

    // Get current user from localStorage
    const currentUser = useMemo(() => {
        try {
            // Try to get from 'employee' key first, fallback to 'user'
            let userData = localStorage.getItem('employee');
            if (!userData) {
                userData = localStorage.getItem('user');
            }
            return JSON.parse(userData || '{}');
        } catch {
            return {};
        }
    }, []);
    const isAdmin = currentUser.privilages === 2;

    const providerNames = useMemo(
        () => [...new Set(servers.map((s) => s.provider_name).filter(Boolean))],
        [servers]
    );

    const monthOptions = useMemo(() => {
        const set = new Set();
        flatUsers.forEach((u) => {
            if (u.activation_date) set.add(String(u.activation_date).slice(0, 7));
        });
        return [...set].sort();
    }, [flatUsers]);

    const filteredServers = useMemo(() => {
        return servers.filter((s) => {
            if (serverFilter !== "all" && String(s.server_port) !== serverFilter) return false;
            if (providerFilter !== "all" && s.provider_name !== providerFilter) return false;
            return true;
        });
    }, [servers, serverFilter, providerFilter]);

    const filteredUsers = useMemo(() => {
        return flatUsers.filter((u) => {
            if (serverFilter !== "all" && String(u.server_port) !== serverFilter) return false;
            if (providerFilter !== "all" && u.provider_name !== providerFilter) return false;
            if (monthFilter !== "all" && String(u.activation_date || "").slice(0, 7) !== monthFilter) return false;
            return true;
        });
    }, [flatUsers, serverFilter, providerFilter, monthFilter]);

    // Storage vs utilization with percentages
    const storageData = useMemo(() => {
        return filteredServers.map((s) => {
            const activeCount = filteredUsers.filter((u) => u.server_port === s.server_port && normalizeStatus(u.status) === "active").length;
            const totalStorage = parseToGB(s.rom);
            const usedStorage = activeCount; // 1 GB per active user
            const percentage = totalStorage > 0 ? Math.round((usedStorage / totalStorage) * 100) : 0;

            return {
                name: s.server_name || `Server ${s.server_port}`,
                totalStorage: totalStorage,
                usedStorage: usedStorage,
                freeStorage: Math.max(0, totalStorage - usedStorage),
                percentage: percentage,
                server_port: s.server_port
            };
        });
    }, [filteredServers, filteredUsers]);

    // Status pie with percentages
    const activeUsers = filteredUsers.filter((u) => normalizeStatus(u.status) === "active");
    const inactiveUsers = filteredUsers.filter((u) => normalizeStatus(u.status) !== "active");
    const totalUsers = activeUsers.length + inactiveUsers.length;

    const pieData = [
        { key: "active", name: `Active Users (${totalUsers > 0 ? Math.round((activeUsers.length / totalUsers) * 100) : 0}%)`, value: activeUsers.length },
        { key: "inactive", name: `Inactive Users (${totalUsers > 0 ? Math.round((inactiveUsers.length / totalUsers) * 100) : 0}%)`, value: inactiveUsers.length },
        { key: "servers", name: `Total Servers (${filteredServers.length})`, value: filteredServers.length },
    ];

    // Provider-wise data with percentages
    const providerLineData = useMemo(() => {
        const map = {};
        filteredUsers.forEach((u) => {
            const provider = u.provider_name || "Unknown";
            if (!map[provider]) map[provider] = { provider, active: 0, inactive: 0, total: 0 };
            if (normalizeStatus(u.status) === "active") map[provider].active += 1;
            else map[provider].inactive += 1;
            map[provider].total += 1;
        });
        return Object.values(map).map(p => ({
            ...p,
            activePercent: p.total > 0 ? Math.round((p.active / p.total) * 100) : 0,
            inactivePercent: p.total > 0 ? Math.round((p.inactive / p.total) * 100) : 0
        }));
    }, [filteredUsers]);

    // Provider storage usage with percentages
    const providerStorageData = useMemo(() => {
        const map = {};
        filteredServers.forEach((s) => {
            const provider = s.provider_name || "Unknown";
            if (!map[provider]) map[provider] = { provider, totalStorage: 0, usedStorage: 0, serverCount: 0 };
            const storage = parseToGB(s.rom);
            map[provider].totalStorage += storage;
            const activeCount = filteredUsers.filter((u) => u.server_port === s.server_port && normalizeStatus(u.status) === "active").length;
            map[provider].usedStorage += activeCount;
            map[provider].serverCount += 1;
        });
        return Object.values(map).map(p => ({
            ...p,
            freeStorage: Math.max(0, p.totalStorage - p.usedStorage),
            percentUsed: p.totalStorage > 0 ? Math.round((p.usedStorage / p.totalStorage) * 100) : 0
        }));
    }, [filteredServers, filteredUsers]);

    const handlePieClick = (entry) => {
        if (entry.key === "active") onShowGroup("Active Users", activeUsers);
        else if (entry.key === "inactive") onShowGroup("Inactive Users", inactiveUsers);
        else if (entry.key === "servers") onShowGroup("All Servers", filteredServers);
    };

    // Custom tooltip for storage data
    const CustomStorageTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            const data = payload[0].payload;
            return (
                <div className="bg-white p-3 border border-slate-200 rounded-lg shadow-lg">
                    <p className="font-semibold text-slate-800">{label}</p>
                    <p className="text-sm text-slate-600">Total Storage: {data.totalStorage} GB</p>
                    <p className="text-sm text-slate-600">Used Storage: {data.usedStorage} GB</p>
                    <p className="text-sm text-slate-600">Free Storage: {data.freeStorage} GB</p>
                    <p className="text-sm font-semibold text-blue-600">Usage: {data.percentage}%</p>
                    <p className="text-xs text-slate-400">Server Port: {data.server_port}</p>
                </div>
            );
        }
        return null;
    };

    return (
        <div className="rounded-xl bg-white border border-slate-200 p-6" style={{ boxShadow: "0 1px 6px rgba(15,23,42,0.06)" }}>
            <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
                <h2 className="text-base font-bold text-slate-800">
                    Server & Usage Overview
                    {!isAdmin && <span className="ml-2 text-xs font-normal text-slate-500">(Employee View)</span>}
                    {isAdmin && <span className="ml-2 text-xs font-normal text-blue-600">(Admin View)</span>}
                </h2>
                <div className="flex gap-2 flex-wrap">
                    <select value={serverFilter} onChange={(e) => setServerFilter(e.target.value)} className="bg-white border border-slate-200 rounded-lg px-3 py-2 text-xs text-slate-700">
                        <option value="all">All Servers</option>
                        {servers.map((s) => (
                            <option key={s.server_port} value={String(s.server_port)}>{s.server_name || s.server_port}</option>
                        ))}
                    </select>
                    <select value={providerFilter} onChange={(e) => setProviderFilter(e.target.value)} className="bg-white border border-slate-200 rounded-lg px-3 py-2 text-xs text-slate-700">
                        <option value="all">All Providers</option>
                        {providerNames.map((p) => (
                            <option key={p} value={p}>{p}</option>
                        ))}
                    </select>
                    <select value={monthFilter} onChange={(e) => setMonthFilter(e.target.value)} className="bg-white border border-slate-200 rounded-lg px-3 py-2 text-xs text-slate-700">
                        <option value="all">All Dates</option>
                        {monthOptions.map((m) => (
                            <option key={m} value={m}>{m}</option>
                        ))}
                    </select>
                </div>
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                {/* 1 — Server storage vs used */}
                <ChartCard
                    title="Server Storage vs Used Storage"
                    subtitle="Scroll inside the chart to see every server"
                    footer={<UsageChips items={storageData} nameKey="name" pctKey="percentage" />}
                >
                    {storageData.length === 0 ? (
                        <div className="h-full flex items-center justify-center text-xs text-slate-400">No data</div>
                    ) : (
                        <ResponsiveContainer width="100%" height={chartHeight(storageData.length)}>
                            <BarChart data={storageData} layout="vertical" margin={{ top: 10, right: 40, left: 10, bottom: 10 }} barCategoryGap="22%" barGap={3}>
                                <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#e2e8f0" />
                                <XAxis type="number" tick={{ fontSize: 11, fill: "#475569" }} unit=" GB" />
                                <YAxis type="category" dataKey="name" width={120} interval={0} tick={{ fontSize: 11, fill: "#334155" }} tickFormatter={(v) => truncate(v, 16)} />
                                <Tooltip content={<CustomStorageTooltip />} cursor={{ fill: "rgba(148,163,184,0.12)" }} />
                                <Legend verticalAlign="top" height={30} wrapperStyle={{ fontSize: 12 }} />
                                <Bar dataKey="totalStorage" name="Total (GB)" fill="#0369a1" radius={[0, 4, 4, 0]} barSize={12}>
                                    <LabelList dataKey="totalStorage" position="right" style={{ fontSize: 10, fill: "#475569" }} />
                                </Bar>
                                <Bar dataKey="usedStorage" name="Used (GB)" fill="#f59e0b" radius={[0, 4, 4, 0]} barSize={12}>
                                    <LabelList dataKey="usedStorage" position="right" style={{ fontSize: 10, fill: "#475569" }} />
                                </Bar>
                                <Bar dataKey="freeStorage" name="Free (GB)" fill="#10b981" radius={[0, 4, 4, 0]} barSize={12}>
                                    <LabelList dataKey="freeStorage" position="right" style={{ fontSize: 10, fill: "#475569" }} />
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    )}
                </ChartCard>

                {/* 2 — Pie */}
                <ChartCard
                    title="Users & Servers Breakdown"
                    subtitle="Click a slice to see the list"
                    footer={
                        <div className="text-center text-xs text-slate-500 mt-3">
                            Total Users: <b>{totalUsers}</b> &nbsp;|&nbsp; Total Servers: <b>{filteredServers.length}</b>
                            {totalUsers > 0 && <> &nbsp;|&nbsp; Active: <b>{Math.round((activeUsers.length / totalUsers) * 100)}%</b></>}
                        </div>
                    }
                >
                    <ResponsiveContainer width="100%" height={CARD_HEIGHT - 4}>
                        <PieChart margin={{ top: 10, right: 10, bottom: 10, left: 10 }}>
                            <Pie
                                data={pieData}
                                dataKey="value"
                                nameKey="name"
                                cx="50%"
                                cy="45%"
                                innerRadius={55}
                                outerRadius={95}
                                paddingAngle={2}
                                label={({ percent }) => `${(percent * 100).toFixed(0)}%`}
                                labelLine={false}
                                onClick={handlePieClick}
                                cursor="pointer"
                            >
                                {pieData.map((entry) => (
                                    <Cell key={entry.key} fill={PIE_COLORS[entry.key]} stroke="#fff" strokeWidth={2} />
                                ))}
                            </Pie>
                            <Tooltip />
                            <Legend verticalAlign="bottom" height={50} wrapperStyle={{ fontSize: 12 }} />
                        </PieChart>
                    </ResponsiveContainer>
                </ChartCard>

                {/* 3 — Provider-wise users */}
                <ChartCard
                    title="Provider-wise User Distribution"
                    subtitle="Active vs inactive users per provider"
                    footer={
                        <div className="flex flex-wrap gap-1.5 mt-3 max-h-16 overflow-y-auto">
                            {providerLineData.map((p) => (
                                <div key={p.provider} className="text-[11px] bg-white border border-slate-200 px-2.5 py-1 rounded-full">
                                    <span className="font-semibold text-slate-700">{truncate(p.provider, 16)}</span>
                                    <span className="ml-1.5 font-bold text-emerald-600">{p.activePercent}% active</span>
                                    <span className="ml-1.5 font-bold text-rose-500">{p.inactivePercent}% inactive</span>
                                </div>
                            ))}
                        </div>
                    }
                >
                    {providerLineData.length === 0 ? (
                        <div className="h-full flex items-center justify-center text-xs text-slate-400">No data</div>
                    ) : (
                        <ResponsiveContainer width="100%" height={chartHeight(providerLineData.length)}>
                            <BarChart data={providerLineData} layout="vertical" margin={{ top: 10, right: 40, left: 10, bottom: 10 }} barCategoryGap="28%" barGap={3}>
                                <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#e2e8f0" />
                                <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11, fill: "#475569" }} />
                                <YAxis type="category" dataKey="provider" width={120} interval={0} tick={{ fontSize: 11, fill: "#334155" }} tickFormatter={(v) => truncate(v, 16)} />
                                <Tooltip
                                    cursor={{ fill: "rgba(148,163,184,0.12)" }}
                                    formatter={(value, name, props) => {
                                        const pct = name === "Active Users" ? props.payload.activePercent : props.payload.inactivePercent;
                                        return [`${value} (${pct}%)`, name];
                                    }}
                                />
                                <Legend verticalAlign="top" height={30} wrapperStyle={{ fontSize: 12 }} />
                                <Bar dataKey="active" name="Active Users" fill="#10b981" radius={[0, 4, 4, 0]} barSize={14}>
                                    <LabelList dataKey="active" position="right" style={{ fontSize: 10, fill: "#475569" }} />
                                </Bar>
                                <Bar dataKey="inactive" name="Inactive Users" fill="#f43f5e" radius={[0, 4, 4, 0]} barSize={14}>
                                    <LabelList dataKey="inactive" position="right" style={{ fontSize: 10, fill: "#475569" }} />
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    )}
                </ChartCard>

                {/* 4 — Provider storage */}
                <ChartCard
                    title="Provider Storage Usage"
                    subtitle="Scroll inside the chart to see every provider"
                    footer={<UsageChips items={providerStorageData} nameKey="provider" pctKey="percentUsed" />}
                >
                    {providerStorageData.length === 0 ? (
                        <div className="h-full flex items-center justify-center text-xs text-slate-400">No data</div>
                    ) : (
                        <ResponsiveContainer width="100%" height={chartHeight(providerStorageData.length)}>
                            <BarChart data={providerStorageData} layout="vertical" margin={{ top: 10, right: 40, left: 10, bottom: 10 }} barCategoryGap="22%" barGap={3}>
                                <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#e2e8f0" />
                                <XAxis type="number" tick={{ fontSize: 11, fill: "#475569" }} unit=" GB" />
                                <YAxis type="category" dataKey="provider" width={120} interval={0} tick={{ fontSize: 11, fill: "#334155" }} tickFormatter={(v) => truncate(v, 16)} />
                                <Tooltip
                                    cursor={{ fill: "rgba(148,163,184,0.12)" }}
                                    formatter={(value, name) => [`${value} GB`, name]}
                                />
                                <Legend verticalAlign="top" height={30} wrapperStyle={{ fontSize: 12 }} />
                                <Bar dataKey="totalStorage" name="Total (GB)" fill="#0369a1" radius={[0, 4, 4, 0]} barSize={12}>
                                    <LabelList dataKey="totalStorage" position="right" style={{ fontSize: 10, fill: "#475569" }} />
                                </Bar>
                                <Bar dataKey="usedStorage" name="Used (GB)" fill="#f59e0b" radius={[0, 4, 4, 0]} barSize={12}>
                                    <LabelList dataKey="usedStorage" position="right" style={{ fontSize: 10, fill: "#475569" }} />
                                </Bar>
                                <Bar dataKey="freeStorage" name="Free (GB)" fill="#10b981" radius={[0, 4, 4, 0]} barSize={12}>
                                    <LabelList dataKey="freeStorage" position="right" style={{ fontSize: 10, fill: "#475569" }} />
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    )}
                </ChartCard>
            </div>
        </div>
    );
};

export default DashboardCharts;